import * as React from 'react'
import { observer } from 'mobx-react'
import Icon from 'components/Icon'
import {
  TableHeader,
  TableHost,
  TableEmptyStateContainer,
  ImageCell,
  DateCell,
} from 'components/Table'
import {
  TableSearchbar,
  TableSearchbarMenuItem,
} from 'payouts/components/TableSearchbar'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownItemTitle,
} from 'components/Dropdown'
import { IconButton } from 'components/IconButton'
import Button from 'components/Button'
import FilterControlsToggleButton from 'components/FilterControlsToggleButton'
import {
  ContextCell,
  AmountCell,
  IdentifierCell,
  FlagsCell,
} from 'payouts/components/PayoutTransactionsTableCells'
import { AdminTransactionHistoryViewModel } from 'payouts/admin/transaction-history/AdminTransactionHistoryViewModel'
import FilterControls from 'infra/FilterControls'
import { useToggle } from 'hooks/state-hooks'
import VirtualTable from '../../../../../components/VirtualTable'

interface HistoricalTransactionsTableProps {
  vm: AdminTransactionHistoryViewModel
}

/**
 * The HistoricalTransactionsTable
 */
export default observer(function HistoricalTransactionsTable({
  vm,
}: HistoricalTransactionsTableProps) {
  const isLoading = vm.transactionsScroller.fetch.pending
  const shouldShowEmptyState = vm.transactions.length === 0
  const filtersShowing = useToggle(false)

  return (
    <>
      <TableHost>
        <TableSearchbar
          searchText={vm.searchText.value}
          onSearchTextChange={vm.searchText.change}
        >
          <TableSearchbarMenuItem>
            <FilterControlsToggleButton
              active={filtersShowing.isEnabled}
              count={vm.filters.appliedFilterCount}
              onClick={filtersShowing.toggle}
            />
          </TableSearchbarMenuItem>
          <TableSearchbarMenuItem>
            <Dropdown
              autoDismiss
              button={
                <IconButton>
                  <Icon name="more" />
                </IconButton>
              }
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                disabled={vm.exportCsv.pending}
                icon={<Icon name="download" />}
                onClick={vm.exportCsv}
              >
                <DropdownItemTitle>Download CSV</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          </TableSearchbarMenuItem>
        </TableSearchbar>
        <FilterControls vm={vm.filters} active={filtersShowing.isEnabled} />

        {isLoading || !shouldShowEmptyState ? (
          <VirtualTable
            totalCount={vm.transactions.length}
            overscan={400}
            scroller={vm.transactionsScroller}
            itemContent={(index) => {
              const tvm = vm.transactions[index]
              return (
                <>
                  <ContextCell
                    source={tvm.transaction.source}
                    userTransactionMemo={tvm.transaction.memo}
                    userTransactionReason={tvm.transaction.reason}
                    jobTransactionJobId={tvm.transaction.jobId}
                  />
                  <ImageCell
                    title={tvm.transaction.recipient.displayName}
                    image={tvm.transaction.recipient.avatar}
                    imageFallback={tvm.transaction.recipient.avatarFallback}
                    minWidth={110}
                  />
                  <AmountCell amount={tvm.transaction.amount} />
                  <ImageCell
                    title={tvm.transaction.workspace.name}
                    image={tvm.transaction.workspace.branding.logos.icon}
                    minWidth={110}
                  />
                  <IdentifierCell value={tvm.transaction.shortId} />
                  <DateCell
                    date={tvm.transaction.completedAt}
                    dataTest={`historical-transaction-date-completed-${tvm.transaction.id}`}
                  />
                  <IdentifierCell value={tvm.transaction.stripeAccountId} />
                  <FlagsCell flags={tvm.transaction.flags} />
                </>
              )
            }}
            headerContent={() => (
              <>
                <TableHeader align="left">Context</TableHeader>
                <TableHeader align="left">Recipient</TableHeader>
                <TableHeader align="right">Amount</TableHeader>
                <TableHeader align="left">Workspace</TableHeader>
                <TableHeader align="left">Transaction short ID</TableHeader>
                <TableHeader align="left">Date completed</TableHeader>
                <TableHeader align="left">Stripe account ID</TableHeader>
                <TableHeader align="left">Flags</TableHeader>
              </>
            )}
          />
        ) : (
          <HistoricalTransactionsEmptyState
            appliedFilters={vm.filtersApplied}
            onClearFilters={vm.clearFilters}
          />
        )}
      </TableHost>
    </>
  )
})

/**
 * Empty state.
 */
function HistoricalTransactionsEmptyState({
  appliedFilters,
  onClearFilters,
}: {
  appliedFilters: boolean
  onClearFilters: () => void
}) {
  return (
    <TableEmptyStateContainer bordered>
      {appliedFilters ? (
        <EmptyState
          title="We couldn't find what you were looking for."
          subtitle={
            <>
              Maybe try{' '}
              <Button size="baseline" type="text" onClick={onClearFilters}>
                clearing your search filter
              </Button>
              ?
            </>
          }
          small
          image={
            <EmptyStateIllustrationImage
              src={require('assets/images/img_search-noresults.svg')}
            />
          }
        />
      ) : (
        <EmptyState
          title="No transactions paid out yet."
          subtitle="When you perform the first successful payout, the paid transactions will appear here."
          small
          image={
            <EmptyStateIllustrationImage
              src={require('assets/images/img_job-completed.svg')}
            />
          }
        />
      )}
    </TableEmptyStateContainer>
  )
}
